import { AutocompleteChangeReason, TextField } from '@mui/material';
import * as R from 'ramda';
import AutoComplete from '../../../../../../../../components/FormInputs/autocompletes/Autocomplete/AutoComplete';
import { useListPick } from '../../../../../../../../components/ListPicker/useListPick';
import LangEnum from '../../../../../../../../lang/LangEnum';
import { onScrollEndReload } from '../../../../../../../../utils/generics';
import { useTranslation } from '../../../../../../../translation/TranslationContext';
import useEmailAutocomplete from './hooks/useEmailAutocomplete';
import { EmailOptions } from './types/EmailAutocomplete.types';

interface EmailAutocompleteProps {
  emails: string[];
  onChangeEmails: (emails: string[]) => void;
  customerId?: string;
}

const EmailAutocomplete = ({ emails, onChangeEmails, customerId }: EmailAutocompleteProps) => {
  const { translate } = useTranslation();

  const listPickActions = useListPick<{ id: string; email: string }>({
    url: `/customers/${customerId}/contacts`,
    limit: 20,
    queryField: 'query',
    disabled: !customerId,
    defaultQuery: {
      withEmail: true,
    },
  });

  const { filterOptions, formattedEmails, handleChangeEmails } = useEmailAutocomplete(
    emails,
    onChangeEmails,
  );

  const options: EmailOptions[] = R.uniqBy(
    (item) => item.email,
    [
      ...formattedEmails,
      ...listPickActions.leftItems.map((contact) => ({ email: contact.email, id: contact.email })),
    ],
  );

  return (
    <AutoComplete<EmailOptions>
      multiple
      loading={listPickActions.loading}
      options={options}
      getOptionLabel={(option: EmailOptions) => option.email}
      value={formattedEmails}
      filterOptions={filterOptions}
      onChange={(
        event: React.ChangeEvent<{}>,
        value: NonNullable<string | EmailOptions> | (string | EmailOptions)[] | null,
        reason: AutocompleteChangeReason,
      ) => handleChangeEmails(event, value as EmailOptions[], reason)}
      slotProps={{
        listbox: {
          style: {
            maxHeight: 248,
          },
          onScroll: (event: React.SyntheticEvent) => onScrollEndReload(event, listPickActions),
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder={translate(LangEnum.GLOBAL_EMAIL_PLACEHOLDER)}
          variant="outlined"
        />
      )}
      renderOption={(option: EmailOptions) =>
        option.prefix ? `${option.prefix} "${option.email}"` : option.email
      }
    />
  );
};

export default EmailAutocomplete;
